import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Loader, Text, TextInput } from '@mantine/core';
import axiosInstance from './../axiosInstance.js';
import { localizeArt } from './../localize.js';
import { formatYear } from './../utils.js';
import { DIMENSION_COLORS } from './ArtTags';

const SEARCH_TYPES = ['movie', 'videoGame', 'book'];
const TYPE_LABELS = {
  movie: { ru: 'Фильм', en: 'Movie' },
  videoGame: { ru: 'Игра', en: 'Game' },
  book: { ru: 'Книга', en: 'Book' },
};

const artTitle = (a, language) => {
  if (!a) return '';
  const name = localizeArt({ ruName: '', ...a }, language).name || a.name || '';
  return name + formatYear(a.year, language);
};

const Cover = ({ art, language }) => (
  <Link to={`/arts/${art.id}`} className="flex flex-col items-center text-center min-w-0" style={{ width: 110 }}>
    {art.thumbnailUrl
      ? <img src={art.thumbnailUrl} alt="" className="rounded" style={{ width: 96, height: 140, objectFit: 'cover' }} />
      : <div className="rounded" style={{ width: 96, height: 140, background: 'var(--mantine-color-default)' }} />}
    <Text size="xs" fw={500} mt={4} lineClamp={2} style={{ wordBreak: 'break-word' }}>{artTitle(art, language)}</Text>
  </Link>
);

// "Сравнить с…": pick a second art (any category) and show the tag
// similarity score plus the shared tags behind it.
const ArtCompare = ({ art, language = 'ru' }) => {
  const ru = language === 'ru';
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [other, setOther] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchSeq = useRef(0);
  const compareSeq = useRef(0);

  useEffect(() => {
    setOpen(false); setTerm(''); setResults(null); setOther(null); setResult(null); setError(null);
  }, [art.id]);

  useEffect(() => {
    const q = term.trim();
    if (!open || other || q.length < 2) { setResults(null); setSearching(false); return; }
    const seq = ++searchSeq.current;
    const timer = setTimeout(() => {
      setSearching(true);
      setResults([]);
      let pending = SEARCH_TYPES.length;
      SEARCH_TYPES.forEach(type => {
        axiosInstance.get(`/arts/search?term=${encodeURIComponent(q)}&artType=${type}&limit=6`)
          .then(r => {
            if (seq !== searchSeq.current) return;
            const rows = (r.data || []).slice(0, 6).map(x => ({ ...x, searchType: type }));
            setResults(prev => [...(prev || []), ...rows]);
          })
          .catch(() => {})
          .finally(() => {
            if (seq !== searchSeq.current) return;
            pending -= 1;
            if (pending === 0) setSearching(false);
          });
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [term, open, other]);

  const compareWith = (b) => {
    const seq = ++compareSeq.current;
    setOther(b);
    setResult(null);
    setError(null);
    setLoading(true);
    axiosInstance.get(`/arts/${art.id}/similarity/${b.id}`)
      .then(r => { if (seq === compareSeq.current) setResult(r.data); })
      .catch(() => { if (seq === compareSeq.current) setError(ru ? 'Не удалось сравнить. Попробуйте ещё раз.' : 'Comparison failed. Try again.'); })
      .finally(() => { if (seq === compareSeq.current) setLoading(false); });
  };

  const pick = (found) => {
    if (found.sourceArt) { compareWith(found.sourceArt); return; }
    // Search hits not yet in the catalog get created the same way the search page does it.
    const seq = ++compareSeq.current;
    setOther({ name: found.enName, ruName: found.ruName || '', year: found.year, thumbnailUrl: found.thumbUrl });
    setResult(null);
    setError(null);
    setLoading(true);
    axiosInstance.post('/arts', { wikiArticleLink: found.pageUrl })
      .then(r => { if (seq === compareSeq.current) compareWith(r.data); })
      .catch(() => {
        if (seq !== compareSeq.current) return;
        setLoading(false);
        setError(ru ? 'Не удалось добавить произведение. Попробуйте другое.' : 'Could not add this work. Try another one.');
      });
  };

  const reset = () => {
    compareSeq.current += 1;
    setOther(null); setResult(null); setError(null); setLoading(false);
  };

  if (!open) {
    return (
      <div className="flex justify-center mt-2">
        <Button size="xs" variant="light" onClick={() => setOpen(true)}>
          {ru ? 'Сравнить с…' : 'Compare with…'}
        </Button>
      </div>
    );
  }

  const shared = (result && result.shared) || [];
  const untagged = result && (!result.taggedA || !result.taggedB);

  return (
    <div className="mx-auto w-full mt-3 p-3 rounded" style={{ maxWidth: 500, border: '1px solid var(--mantine-color-default-border)' }}>
      {!other && (
        <>
          <div className="flex items-center gap-2">
            <TextInput
              className="flex-1"
              size="sm"
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.currentTarget.value)}
              placeholder={ru ? 'Фильм, игра или книга…' : 'Movie, game or book…'}
              rightSection={searching ? <Loader size="xs" /> : null}
            />
            <Button size="sm" variant="subtle" color="gray" onClick={() => { setOpen(false); setTerm(''); }}>
              {ru ? 'Отмена' : 'Cancel'}
            </Button>
          </div>
          {results && results.length > 0 && (
            <div className="mt-2 flex flex-col" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {results.map((f, i) => (
                <button
                  key={`${f.searchType}-${f.pageUrl}-${i}`}
                  type="button"
                  onClick={() => pick(f)}
                  className="flex items-center gap-3 p-1 rounded text-left"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  {f.thumbUrl || (f.sourceArt && f.sourceArt.thumbnailUrl)
                    ? <img src={(f.sourceArt && f.sourceArt.thumbnailUrl) || f.thumbUrl} alt="" className="rounded flex-shrink-0" style={{ width: 32, height: 46, objectFit: 'cover' }} />
                    : <div className="rounded flex-shrink-0" style={{ width: 32, height: 46, background: 'var(--mantine-color-default)' }} />}
                  <div className="min-w-0 flex-1">
                    <Text size="sm" className="truncate">
                      {((language === 'en' || !f.ruName) ? f.enName : f.ruName) + formatYear(f.year, language)}
                    </Text>
                    <Text size="xs" c="dimmed" className="truncate">
                      {[TYPE_LABELS[f.searchType][ru ? 'ru' : 'en'], f.author].filter(Boolean).join(' • ')}
                    </Text>
                  </div>
                </button>
              ))}
            </div>
          )}
          {results && results.length === 0 && !searching && (
            <Text size="sm" c="dimmed" ta="center" mt="sm">{ru ? 'Ничего не найдено' : 'Nothing found'}</Text>
          )}
        </>
      )}

      {other && (
        <>
          <div className="flex items-start justify-center gap-3">
            <Cover art={art} language={language} />
            <div className="flex flex-col items-center justify-center" style={{ height: 140, minWidth: 72 }}>
              {loading && <Loader size="sm" />}
              {result && (
                <span style={{ fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{result.percent}%</span>
              )}
            </div>
            {other.id
              ? <Cover art={other} language={language} />
              : (
                <div className="flex flex-col items-center text-center min-w-0" style={{ width: 110 }}>
                  {other.thumbnailUrl
                    ? <img src={other.thumbnailUrl} alt="" className="rounded" style={{ width: 96, height: 140, objectFit: 'cover' }} />
                    : <div className="rounded" style={{ width: 96, height: 140, background: 'var(--mantine-color-default)' }} />}
                  <Text size="xs" fw={500} mt={4} lineClamp={2}>{artTitle(other, language)}</Text>
                </div>
              )}
          </div>

          {error && <Text size="sm" c="red" ta="center" mt="sm">{error}</Text>}

          {result && untagged && (
            <Text size="sm" c="dimmed" ta="center" mt="sm">
              {ru ? 'У одного из произведений пока нет тегов — сравнение неточное' : 'One of the works has no tags yet, so the comparison is unreliable'}
            </Text>
          )}
          {result && !untagged && shared.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" mt="sm">{ru ? 'Ничего общего' : 'Nothing in common'}</Text>
          )}
          {result && shared.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-1 mt-3">
              <Text size="xs" c="dimmed" className="mr-1">{ru ? 'Общее:' : 'Shared:'}</Text>
              {shared.map(t => (
                <Badge key={`${t.dimension}:${t.value}`} color={DIMENSION_COLORS[t.dimension] || 'gray'} variant="light" size="sm" radius="sm" style={{ textTransform: 'none' }}>
                  {ru && t.ru ? t.ru : t.value}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex justify-center gap-2 mt-3">
            <Button size="xs" variant="light" onClick={reset}>
              {ru ? 'Сравнить с другим' : 'Compare with another'}
            </Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => { reset(); setOpen(false); setTerm(''); }}>
              {ru ? 'Закрыть' : 'Close'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ArtCompare;
